import { Controller } from '@hotwired/stimulus';
import { sanitizePastedHtml } from '@/utils/action_text_sanitizer';

const acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

export default class extends Controller {
  static targets = ['errorDisplay', 'localeOverlay'];

  static values = {
    fileTypeError: String,
    imageSizeError: String,
    pdfSizeError: String,
    pasteRejectedMessage: String,
    allowExternalImages: Boolean,
    disabled: Boolean,
  };

  displayErrorMessage(message) {
    this.errorDisplayTargets.forEach((target) => {
      target.innerText = message;
      target.hidden = false;
    });
  }

  hideErrorMessage() {
    this.errorDisplayTargets.forEach((target) => {
      target.hidden = true;
    });
  }

  checkFileAcceptance(event) {
    if (!acceptedTypes.includes(event.file.type)) {
      event.preventDefault();
      this.displayErrorMessage(this.fileTypeErrorValue);
      return;
    }

    switch (event.file.type) {
      case 'application/pdf':
        if (event.file.size > 10485760) {
          event.preventDefault();
          this.displayErrorMessage(this.pdfSizeErrorValue);
        }
        break;
      default:
        if (event.file.size > 2097152) {
          event.preventDefault();
          this.displayErrorMessage(this.imageSizeErrorValue);
        }
    }
  }

  handleFileUpload(event) {
    this.hideErrorMessage();
    this.checkFileAcceptance(event);
  }

  handleTrixBeforePaste(event) {
    const html = event.paste?.html;
    if (!html) return;

    const { html: sanitized, removedAny } = sanitizePastedHtml(html, {
      allowExternalImages: this.allowExternalImagesValue,
    });

    if (!removedAny) {
      const hasStalePasteFlash = this.errorDisplayTargets.some(
        (target) => target.innerText.trim() === this.pasteRejectedMessageValue,
      );
      if (hasStalePasteFlash) this.hideErrorMessage();
      return;
    }

    event.paste.html = sanitized;
    if (this.pasteRejectedMessageValue) {
      this.displayErrorMessage(this.pasteRejectedMessageValue);
    }
  }

  handleTrixInitialize() {
    if (this.disabledValue !== true) return;

    this.element.querySelectorAll('trix-editor').forEach((trixEditor) => {
      trixEditor.editor.element.setAttribute('contentEditable', false);

      const toolbarButtons =
        trixEditor.toolbarElement.querySelectorAll('button');
      toolbarButtons.forEach((button) => {
        if (button.dataset.trix) {
          button.disabled = true;
          button.removeAttribute('data-trix-attribute');
          button.removeAttribute('data-trix-key');
          button.removeAttribute('data-trix-action');
        }
      });
    });
  }

  updateOverlay(event) {
    this.localeOverlayTarget.textContent = event.params.locale;
  }

  switchLocaleInput(event) {
    this.element.querySelectorAll('[data-locale-input]').forEach((element) => {
      element.hidden =
        element.getAttribute('data-locale-input') !== event.params.locale;
    });
  }
}
